/**
 * Client portal rules (phase 5, docs/specs/portal/client-area.md): project progress as the Job Summary computes it,
 * report labels, the Storage path of a published report PDF, and who may publish, preview and manage viewers.
 *
 * Progress per block and for the project, from the reviewed line status (ADR-013):
 * - planned km: Σ planned length of the original lines (`planProgress`; reflights and patches never add);
 * - accepted km: Σ `accepted_m` of reviewed, `use_km` statuses (`acceptedM`, the Job Summary's Total Accepted);
 * - remaining km: `planProgress` remaining (decision 29, the Job Summary's Est KM Remaining), with the lines of a
 *   block ticked "Job km are completed" counting 0 (N32, `withCompletedBlocks`), as Teslyn Field's Project overview
 *   and the Job Summary count them;
 * - % complete: `estPercentComplete` (truncated, as TJR prints it).
 * The project totals equal `jobSummary(…, tjrFromTeslyn(…))`.
 */
import { withCompletedBlocks } from './block-report.js';
import { formatKm } from './km.js';
import type { Block, LineStatus, PlannedLine, Report } from './model.js';
import { planProgress } from './progress.js';
import { acceptedM } from './tjr-adapter.js';
import { estPercentComplete } from './tjr-summary.js';

export interface ProgressFigures {
  readonly plannedKm: number;
  readonly acceptedKm: number;
  readonly remainingKm: number;
  /** Whole per cent, truncated. */
  readonly percentComplete: number;
}

export interface BlockProgress extends ProgressFigures {
  readonly blockId: string;
  readonly name: string;
}

export interface ProjectProgress {
  readonly total: ProgressFigures;
  /** Blocks with at least one planned line, in the blocks' order. */
  readonly blocks: readonly BlockProgress[];
  /** Live statuses whose planned line is not in the plan (counted in the total only). */
  readonly unplacedStatuses: number;
}

function figures(plannedM: number, acceptedMetres: number, remainingM: number): ProgressFigures {
  const plannedKm = plannedM / 1000;
  const acceptedKm = acceptedMetres / 1000;
  return {
    plannedKm,
    acceptedKm,
    remainingKm: remainingM / 1000,
    percentComplete: estPercentComplete(acceptedKm, plannedKm),
  };
}

/**
 * Accepted, remaining and % complete per block and for the project. A block ticked "Job km are completed" (N32)
 * has 0 km remaining, and the project's remaining km leave its lines out; its accepted km and % complete stay what
 * was flown.
 */
export function projectProgress(
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  blocks: readonly Pick<Block, 'id' | 'name' | 'deleted_at' | 'km_completed'>[],
): ProjectProgress {
  const live = statuses.filter((s) => s.deleted_at === null);
  const progress = withCompletedBlocks(
    planProgress(live, plan),
    blocks.filter((b) => b.deleted_at === null),
  );
  const lineBlock = new Map(plan.filter((p) => p.deleted_at === null).map((p) => [p.id, p.block_id]));
  const numberBlock = new Map(
    plan.filter((p) => p.deleted_at === null).map((p) => [p.number, p.block_id] as const),
  );
  const statusesOf = new Map<string, LineStatus[]>();
  let unplaced = 0;
  for (const s of live) {
    const b = lineBlock.get(s.planned_line_id) ?? numberBlock.get(s.line_number);
    if (b === undefined) {
      unplaced += 1;
      continue;
    }
    statusesOf.set(b, [...(statusesOf.get(b) ?? []), s]);
  }
  const rows: BlockProgress[] = [];
  for (const block of blocks) {
    if (block.deleted_at !== null) continue;
    const lines = progress.lines.filter((l) => l.original.block_id === block.id);
    if (lines.length === 0) continue;
    const planned = lines.reduce((a, l) => a + l.original.length_m, 0);
    const remaining = lines.reduce((a, l) => a + l.remaining_m, 0);
    rows.push({
      blockId: block.id,
      name: block.name,
      ...figures(planned, acceptedM(statusesOf.get(block.id) ?? []), remaining),
    });
  }
  return {
    total: figures(progress.totalPlannedM, acceptedM(live), progress.remainingM),
    blocks: rows,
    unplacedStatuses: unplaced,
  };
}

/** "1,234.56 km". */
export const kmText = (km: number): string => `${formatKm(km)} km`;

// The period and label words ("16 Oct 2026", "Daily report, 16 Oct 2026") are in report-label.ts, which imports
// nothing, so the Edge Functions' emails use the same words (F07).
export { periodText, reportLabel, staffReportLabel, type ReportLabelKind } from './report-label.js';

/**
 * The newer revision that took an unpublished report's place for the client (decision 241: a newer revision of a
 * published report is published in its place and the older one withdrawn, `20261020040000_report_revision_replaces`):
 * the highest published revision above it of the same kind and period; null when there is none or it is published
 * itself. `published` is null for a report with no PDF in the cloud.
 */
export function replacedForClientBy(
  report: Pick<Report, 'id' | 'kind' | 'period' | 'revision'> & { readonly published: boolean | null },
  reports: readonly (Pick<Report, 'id' | 'kind' | 'period' | 'revision'> & {
    readonly published: boolean | null;
  })[],
): number | null {
  if (report.published) return null;
  const newer = reports
    .filter(
      (r) =>
        r.id !== report.id &&
        r.published === true &&
        r.kind === report.kind &&
        r.period === report.period &&
        r.revision > report.revision,
    )
    .map((r) => r.revision);
  return newer.length ? Math.max(...newer) : null;
}

/** Where the Reports page keeps the project last chosen in this browser, per person and per portal (R198). */
export function reportsProjectKey(personId: string | null, scopeKey: string): string {
  return `teslyn.cloud.reports-project.v1.${personId ?? 'signed-out'}.${scopeKey}`;
}

type ChoosableProject = { readonly id: string; readonly status: string };

/**
 * The project the Reports page opens on when it is known without reading any report (R198, MB 4 Nov 2026): the one a
 * link names (a report email's or a client notice's `?page=reports&project=<id>`), else the one last chosen in this
 * browser; each only while it is listed. Null: choose by the newest report (`reportsProjectByNewest`).
 */
export function reportsProjectChosen(
  projects: readonly ChoosableProject[],
  linked: string | null,
  remembered: string | null,
): string | null {
  const listed = (id: string | null): id is string => id !== null && projects.some((p) => p.id === id);
  if (listed(linked)) return linked;
  if (listed(remembered)) return remembered;
  return null;
}

/**
 * The project the Reports page opens on otherwise (R198): the active project with the newest report, else the newest
 * report's project, else the first active project, else the first; null with no project. `newestFirst` is the
 * reports the person may see, newest first (as the Reports list orders them).
 */
export function reportsProjectByNewest(
  projects: readonly ChoosableProject[],
  newestFirst: readonly { readonly projectId: string }[],
): string | null {
  const byId = new Map(projects.map((p) => [p.id, p]));
  const active = newestFirst.find((r) => byId.get(r.projectId)?.status === 'active');
  if (active) return active.projectId;
  const any = newestFirst.find((r) => byId.has(r.projectId));
  if (any) return any.projectId;
  return (projects.find((p) => p.status === 'active') ?? projects[0])?.id ?? null;
}

/** The report kinds the client portal shows (phase 5: daily and weekly). */
export const CLIENT_REPORT_KINDS: readonly Report['kind'][] = ['daily', 'weekly'];

/**
 * Where a published report's PDF lives in Supabase Storage (bucket `reports`, ADR-016 layout). The database refuses
 * any other path (`report_publication` check and the Storage policies).
 */
export function reportStoragePath(tenantId: string, projectId: string, reportId: string): string {
  return `tenants/${tenantId}/projects/${projectId}/reports/${reportId}.pdf`;
}

export type PortalAction =
  /** Attach a PDF to a report, publish it to the project's client organisation, unpublish it. */
  | 'report.publish'
  /** Preview the portal as a client organisation sees it (MB decision 9). */
  | 'portal.preview'
  /** Invite and remove viewers of one's own client organisation. */
  | 'viewers.manage'
  /**
   * Link a project to the client organisation whose portal shows it (`project.client_org_id`), or unlink it. Nothing
   * published or shared reaches a client until the project is linked (MB's R99, 26 Oct 2026).
   */
  | 'project.link_client'
  /**
   * "Publish hazards to the client portal" and "Show the landholder boundaries to the client" for a project (Diurnal
   * parity 05-025; decisions 82, 83): every staff role, as for the pilot hazard pack the hazards come from (decision
   * 625) and the refly list (decision 543). Database: `portal_map_set` (`20261027012000_portal_map.sql`).
   */
  | 'portal.map'
  /**
   * Add, show to the client, hide and withdraw a project's share links (ADR-008; 04-security-and-tenancy.md §2: tenant
   * admins and data processors manage share links; managers as for reports). Database: `share_link_writer`
   * (`20261102190000_deliverables.sql`).
   */
  | 'share_link.manage'
  /**
   * Add versions of a project's Safety documents, show them to the client, hide and remove them (R162, decision 926:
   * "as with reports", decisions 184 and 241). Database: `is_report_publisher` in the safety_document functions.
   */
  | 'safety.publish';

const PORTAL_RULES: Readonly<Record<PortalAction, readonly string[]>> = {
  // the report writers (office staff never write reports, 04-security-and-tenancy.md §2)
  'report.publish': ['tenant_admin', 'manager', 'data_processor', 'field_crew'],
  'portal.preview': ['tenant_admin', 'manager'],
  'viewers.manage': ['client_admin'],
  // the project writers (policy project_write, 20261018001200_office_staff_setup.sql)
  'project.link_client': ['tenant_admin', 'manager', 'office_staff'],
  'portal.map': ['tenant_admin', 'manager', 'field_crew', 'office_staff', 'data_processor'],
  'share_link.manage': ['tenant_admin', 'manager', 'data_processor'],
  'safety.publish': ['tenant_admin', 'manager', 'data_processor', 'field_crew'],
};

/**
 * Whether any of `roles` allows a portal action. Display only: `is_report_publisher`, `portal_preview_audit`, the
 * client_membership policies (`20261019100000_client_portal.sql`) and `project_write` enforce the same table.
 */
export function canPortal(roles: readonly string[], action: PortalAction): boolean {
  const allowed = PORTAL_RULES[action];
  return roles.some((r) => allowed.includes(r));
}

export function portalRolesFor(action: PortalAction): readonly string[] {
  return PORTAL_RULES[action];
}
